import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, MenuItem, Typography, Checkbox } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import CustomDropdown from '../../../components/customDropdown';
import {
  ApplicationQuestionAndAnswerDto,
  CustomQuestionTypeEnum,
  CustomQuestionResponseTypeEnum,
  ApplicationOccupantDtoGovIdTypeEnum,
  UpsertApplicantV2Dto,
  ApplicationQuestionResponseV2Dto,
} from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { CustomDropZone } from '../../../pmui/onboarding/components/CustomDropZone';
import { uploadDocuments, selectDocumentState, deleteDocument } from '../../../shared/sagas/document/documentSlice';
import { applicationStyles } from './CustomStyles';
import { convertSqlDateToUSCalendarDate, convertUSCalendarDateToSqlDate } from '../../../shared/util/converters';
import { InosioDateFormat, InosioSsnFormat } from '../../../shared/util/formaters';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { applicantDocumentMetadata, getApplicantV2, selectApplicantsState, updateApplicantV2, updateCustomQuestionsAnswersV2 } from '../sagas/applicants/applicantsSlice';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { DobRegex, E164PhoneRegex, SsnRegex } from '../../../utils/regex';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import CustomQuestionsSection from './CustomQuestionsSection';
import { compareDesc, subYears } from 'date-fns';
import { PhoneNumberInput } from '../../../shared/PhoneNumberInput';

const schemaValidationApplicantPersonalInfo = Yup.object({
  FirstName: Yup.string().max(100, 'Max length is 100 characters').required('First Name Required'),
  LastName: Yup.string().max(100, 'Max length is 100 characters').required('Last Name Required'),
  MiddleName: Yup.string().max(100, 'Max length is 100 characters').nullable(),
  Dob: Yup.string()
    .min(10, 'Date of birth must be in this format MM/DD/YYYY')
    .max(10, 'Date of birth must be in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
    .test({
      name: 'Applicant minimum age',
      exclusive: true,
      message: 'Applicants/coapplicants must be 18 years or older.',
      test: (value, testContext) => {
        if (testContext.parent.Dob) {
          const applicantAge = new Date(testContext.parent.Dob);
          const _18yrsAgoToday = subYears(new Date(), 18);

          const is18OrOlder = compareDesc(applicantAge, _18yrsAgoToday);
          return is18OrOlder > -1;
        }
        return false;
      },
    })
    .required('Please enter a date of birth'),
  PhoneNumber: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
  IDontHaveSsn: Yup.boolean(),
  Ssn: Yup.string()
    .nullable()
    .when('IDontHaveSsn', {
      is: false,
      then: Yup.string()
        .min(11, 'SSN must be in this format XXX-XX-XXXX')
        .max(11, 'SSN must be in this format XXX-XX-XXXX')
        .matches(SsnRegex, 'SSN must be in this format XXX-XX-XXXX')
        .required('Required SSN XXX-XX-XXXX')
        .nullable(),
      otherwise: Yup.string().nullable(),
    }),
  GovIdType: Yup.string().nullable(), //shouldn't these be required ? ask @Ramsey
  GovIdNumber: Yup.string().nullable(),
});

type schemaValidationApplicantPersonalInfoType = Yup.InferType<typeof schemaValidationApplicantPersonalInfo>;

export default function PersonalInfo() {
  const query = useQuery();
  const classes1 = applicationStyles();
  const hasValidationQueryParam = !!query.get('validation');
  const classes = applicationStyles();
  const dispatch = useDispatch();

  const { ipaUser } = useSelector(selectAuthState);
  const {
    Applicant,
    isFetchingApplicantSuccess,
    isFetchingApplicantFailure,
    fetchDocumentResponse,
    isFetchingDocumentsSuccess,
    isFetchingDocumentsFailed,
    CustomQuestionAnswers,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    Application,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isUpdatingApplicantSuccess,
    isUpdatingApplicantFailed,
    updatingApplicantError,
  } = useSelector(selectApplicantsState);
  const { isUploadingDocuments, isUploadingDocumentsSuccess, isUploadingDocumentsFailed } = useSelector(selectDocumentState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  const [localError, setLocalError] = useState<string | null>(null);
  const [IDFiles, setIDFiles] = useState<Array<any>>([]); //really bad to use any... but i don't want to break the rest of the app just yet
  const [personalInfoCustomQuestionsAndAnswers, setPersonalInfoCustomQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const requireIdFilesUpload = !!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsRequirePersonalDocsUpload;

  //get the applicant on load
  useEffect(() => {
    dispatch({ type: getApplicantV2.type });
  }, [dispatch, ipaUser]);

  //deal with the documents once received
  useEffect(() => {
    if (isFetchingDocumentsSuccess) {
      const usefulData = fetchDocumentResponse
        ?.filter((d) => d.documentTypeId === 3) //filter for gov ids
        .map((d) => {
          const file: applicantDocumentMetadata = {
            name: d.fileName, //is just this good enough?
            remoteUuid: d.remoteUuid,
          };
          // as File;
          return file;
        });
      setIDFiles(usefulData);
    }
  }, [isFetchingDocumentsSuccess, fetchDocumentResponse]);

  //populate custom questions once prop group details received
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationPersonalInfoCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.PersonalV1,
      );
      setPersonalInfoCustomQuestionsAndAnswers(
        organizationPersonalInfoCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === Applicant.Uuid);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: Applicant.Uuid,
            BelongsToEntityUuid: Applicant.Uuid,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, isFetchingApplicantSuccess, isFetchingCustomQuestionAnswersSuccess]);

  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess && isFetchingDocumentsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingApplicantFailure || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure || isFetchingDocumentsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the personal information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicantSuccess,
    isFetchingApplicantFailure,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingDocumentsSuccess,
    isFetchingDocumentsFailed,
  ]);

  //show/hide error if document upload failed
  useEffect(() => {
    if (isUploadingDocumentsFailed) {
      setLocalError('Error uploading document(s).');
    }
  }, [isUploadingDocumentsSuccess, isUploadingDocumentsFailed]);

  useEffect(() => {
    if (isUpdatingApplicantFailed) {
      setLocalError(updatingApplicantError.message ?? 'Error saving information.');
    }

    if (isUpdatingApplicantSuccess) {
      setLocalError(null);
    }
  }, [isUpdatingApplicantSuccess, isUpdatingApplicantFailed]);

  const formik = useFormik<schemaValidationApplicantPersonalInfoType>({
    initialValues: {
      FirstName: Applicant.FirstName ?? '',
      LastName: Applicant.LastName ?? '',
      MiddleName: Applicant.MiddleName ?? '',
      Dob: Applicant.Dob ? convertSqlDateToUSCalendarDate(Applicant.Dob) : '',
      PhoneNumber: Applicant.PhoneNumber ?? '',
      Ssn: Applicant.Ssn ?? '',
      GovIdType: Applicant.GovIdType,
      GovIdNumber: Applicant.GovIdNumber ?? '',
      IDontHaveSsn: !!Applicant.HasNoSsn,
    },
    validationSchema: schemaValidationApplicantPersonalInfo,

    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handleIDFilesAdded = (filesToUpload: File[]) => {
    let files = IDFiles;

    //check if current file count + newly dropped files is eq or less than 3
    if (files.length + filesToUpload.length <= 3) {
      filesToUpload.forEach((af: File) => {
        //for each new file dropped, only allow it that same file name doesn't already exist in our set
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      setIDFiles(files);

      const uploadingFiles = files?.filter((f) => f.name && !f.remoteUuid);
      if (uploadingFiles.length > 0) {
        dispatch({
          type: uploadDocuments.type,
          payload: {
            belongsToEntityUuid: ipaUser?.currentProfile.uuid,
            documents: uploadingFiles,
            documentType: 'GovIssuedID',
          },
        });
      }
    }
  };

  const handleIDFilesDelete = (file: any) => {
    if (file.remoteUuid) {
      dispatch(deleteDocument(file.remoteUuid));
    } else {
      //there is no remoteUuid, so it must not have even uploaded. Just remove it from local state.
      const newList = IDFiles.filter((f) => f.name !== file.name);
      setIDFiles(newList);
    }
  };

  /**
   * Updatds the DOB and stores it into formik in the format we want.
   * @param dob
   */
  const formatDob = (dob: string) => {
    const formattedDob = InosioDateFormat(dob);
    formik.setFieldValue('Dob', formattedDob);
  };

  /**
   * Updates the SSN and stores it into formik in a way we want.
   * @param ssn
   */
  const formatSsn = (ssn: string) => {
    const formattedSsn = InosioSsnFormat(ssn);
    formik.setFieldValue('Ssn', formattedSsn);
  };

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = personalInfoCustomQuestionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: Applicant.Uuid, //all personal questions belong to this applicant (vs an employment etc)
            ApplicantUuid: Applicant.Uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setPersonalInfoCustomQuestionsAndAnswers(
      personalInfoCustomQuestionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  const handleFormValuesChanged = (e: any) => {
    /*
     */

    const payload = {
      FirstName: !formik.errors.FirstName ? formik.values.FirstName : undefined,
      MiddleName: !formik.errors.MiddleName ? formik.values.MiddleName || undefined : undefined,
      LastName: !formik.errors.LastName ? formik.values.LastName : undefined,
      Dob: !formik.errors.Dob ? convertUSCalendarDateToSqlDate(formik.values.Dob) : undefined,
      PhoneNumber: !formik.errors.PhoneNumber ? formik.values.PhoneNumber : undefined,
      Ssn: !formik.errors.Ssn ? formik.values.Ssn || null : undefined,
      HasNoSsn: !formik.errors.IDontHaveSsn ? formik.values.IDontHaveSsn : undefined,
      GovIdType: !formik.errors.GovIdType ? formik.values.GovIdType || undefined : undefined,
      GovIdNumber: !formik.errors.GovIdNumber ? formik.values.GovIdNumber || undefined : undefined,
    } as UpsertApplicantV2Dto;

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateApplicantV2.type,
      payload: {
        applicantUuid: Applicant.Uuid,
        updateDto: payload,
      },
    });
  };

  return (
    <Box sx={{ width: '100%', minHeight: '60vh' }}>
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box p={4} minHeight="inherit" width="100%">
          <Grid container spacing={4}>
            {localError && (
              <Grid item xs={12}>
                <Typography className={classes.globalText}>{localError}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Tell us about yourself
              </Typography>
              <InputField
                label={'First Name'}
                isError={!!formik.errors?.FirstName && hasValidationQueryParam}
                errors={formik.errors?.FirstName}
                touched={formik.touched?.FirstName}
                {...formik.getFieldProps('FirstName')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.FirstName}
              />
              <InputField
                label={'Middle Name'}
                isError={false}
                errors={formik.errors?.MiddleName}
                touched={formik.touched?.MiddleName}
                {...formik.getFieldProps('MiddleName')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              />
              <InputField
                label={'Last Name'}
                isError={!!formik.errors?.LastName && hasValidationQueryParam}
                errors={formik.errors?.LastName}
                touched={formik.touched?.LastName}
                showAsterisk={!formik.values.LastName}
                {...formik.getFieldProps('LastName')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              />
              <PhoneNumberInput
                {...formik.getFieldProps('PhoneNumber')}
                showErrorMessage={!!formik.errors?.PhoneNumber && !!formik.touched?.PhoneNumber}
                onChange={(phone) => {
                  formik.setFieldValue('PhoneNumber', phone.startsWith('+') ? phone : `+${phone}`);
                }}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showErrorPinkBackground={!!formik.errors?.PhoneNumber && hasValidationQueryParam}
                errorMessage={formik.errors?.PhoneNumber}
                showAsterisk={true}
              />
              {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
              <Box sx={{ marginTop: '16px' }}></Box>
              <InputField
                label={'Date of Birth'}
                placeholder="MM/DD/YYYY"
                isError={!!formik.errors?.Dob && hasValidationQueryParam}
                errors={formik.errors?.Dob}
                touched={formik.touched?.Dob}
                {...formik.getFieldProps('Dob')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                onChange={(e) => formatDob(e.target.value)}
                showAsterisk={!formik.values.Dob}
              />
              <InputField
                disabled={formik.values.IDontHaveSsn}
                label={'Social Security No'}
                placeholder="XXX-XX-XXXX"
                isError={!!formik.errors?.Ssn && hasValidationQueryParam}
                errors={formik.errors?.Ssn}
                touched={formik.touched?.Ssn} //by not doing formik.handleBlur, it never set this to true.. thus never showing the error text
                {...formik.getFieldProps('Ssn')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                onChange={(e) => {
                  formatSsn(e.target.value);
                }}
                showAsterisk={!formik.values.IDontHaveSsn}
              />
              <Typography sx={{ mb: -2 }} className={classes1.bodyTextSmall}>
                <Checkbox
                  name={'IDontHaveSsn'}
                  inputProps={{ 'aria-label': 'controlled' }}
                  checked={formik.values.IDontHaveSsn}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  onChange={(e) => {
                    formik.setFieldValue('Ssn', '');
                    formik.handleChange(e); // Update formik state
                  }}
                  sx={{ ml: 1 }}
                />
                I don't have an SSN
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Upload a government issued ID:
              </Typography>
              <CustomDropdown
                isFullWidth
                label={'GovIdType'}
                value={formik.values.GovIdType ?? ''}
                error={formik.errors?.GovIdType || (hasValidationQueryParam && !formik.values.GovIdType ? 'Please select an option' : undefined)}
                handleChange={(e) => {
                  formik.setFieldValue('GovIdType', e.target.value);
                }}
                menuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
                showAsterisk={!formik.values.GovIdType}
                handleBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              >
                <MenuItem style={{ height: '36px' }} key={ApplicationOccupantDtoGovIdTypeEnum.Passport} value={ApplicationOccupantDtoGovIdTypeEnum.Passport}>
                  Passport
                </MenuItem>
                <MenuItem style={{ height: '36px' }} key={ApplicationOccupantDtoGovIdTypeEnum.Id} value={ApplicationOccupantDtoGovIdTypeEnum.Id}>
                  ID
                </MenuItem>
                <MenuItem style={{ height: '36px' }} key={ApplicationOccupantDtoGovIdTypeEnum.DriversLicense} value={ApplicationOccupantDtoGovIdTypeEnum.DriversLicense}>
                  License
                </MenuItem>
              </CustomDropdown>
              <InputField
                label={'ID Number'}
                isError={!!formik.errors?.GovIdNumber || (hasValidationQueryParam && !formik.values.GovIdNumber)}
                errors={formik.errors?.GovIdNumber}
                touched={formik.touched?.GovIdNumber}
                {...formik.getFieldProps('GovIdNumber')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.GovIdNumber}
              />

              {/* maxFileSize must be in MB, example: maxFileSize={20} */}
              <CustomDropZone
                maxFileSize={20}
                files={IDFiles}
                fileLimit={3}
                handleFilesAdded={handleIDFilesAdded}
                handleFileDeleted={handleIDFilesDelete}
                description="Up to 20MB per file. Up to 3 Files at a time."
                height="250px"
                fileTypes={['image/png', 'image/jpeg', 'application/pdf']} //.txt
                validationError={hasValidationQueryParam && requireIdFilesUpload && !IDFiles.length} //User is in validation mode and they havent uploaded any personal docs even though the PM requires it
                startProgress={isUploadingDocuments}
                stopProgress={isUploadingDocumentsSuccess}
                syncProgress={true}
              />
              <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop: '10px' }}>{'Supported File Types: .jpg, .pdf, .png'}</Typography>
            </Grid>
            <CustomQuestionsSection
              questionsAndAnswers={personalInfoCustomQuestionsAndAnswers}
              hasValidationQueryParam={hasValidationQueryParam}
              onFieldChange={handleCustomQuestionFieldChanged}
              onFieldUpdate={handleSaveCustomQuestionAnswer}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
}
